import { useNavigate, useRevalidator } from 'react-router';
import { motion } from 'motion/react';

import type { Route } from './+types/start';
import type { Audience } from '@/cms/topics';
import { metaFor } from '@/cms/meta';
import { stagedChild } from '@/components/motion/variants';
import { ChoiceTile } from '@/components/onboarding/choice-tile';
import { useOnboardingBack } from '@/components/onboarding/onboarding-chrome';
import {
  ONBOARDING_STEPS,
  OnboardingHeading,
  PartnerMark,
  StepCounter,
  StepLogo,
} from '@/components/onboarding/onboarding-content';
import { useRootLoaderData } from '@/hooks/use-root-loader-data';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { courseLanguage, themeForAudience, useUserStore } from '@/store/user';

export function meta({ matches }: Route.MetaArgs) {
  const root = matches[0]?.data;

  return root
    ? metaFor({ siteSettings: root.siteSettings, title: root.strings.onboarding.meta_start, path: '/start' })
    : [{ title: 'Beach Passport' }];
}

/** The flow's second question. Language is step 1, on the welcome screen. */
const STEP = 2;

/**
 * The age question, and nothing else. Picking an age persists the profile —
 * with the language the welcome screen already chose — and moves on to the name
 * screen.
 *
 * This screen used to ask language *and* age, swapping one for the other inside
 * a `StepStage`. Language moved to `/`, where a learner who reads no English
 * meets it on the first screen rather than one tap in, so the swap and its
 * step-local back affordance went with it: back here means the welcome screen,
 * and the layout's own `StepStage` is the only one in the flow again.
 */
export default function Start() {
  const { onboarding, content } = useStrings();
  const { language, siteSettings } = useRootLoaderData();

  const setProfile = useUserStore((state) => state.setProfile);

  const navigate = useNavigate();
  const revalidator = useRevalidator();

  useOnboardingBack({ to: '/', label: content.content_back });

  const audiences: Array<{ value: Audience; label: string }> = [
    { value: 'child', label: onboarding.audience_child },
    { value: 'teen', label: onboarding.audience_teen },
    { value: 'adult', label: onboarding.audience_adult },
  ];

  const chooseAudience = (audience: Audience) => {
    // `language` is the root loader's active code, which is what the welcome
    // screen's picker wrote to the cookie. A child is switched to English here
    // (see `courseLanguage`).
    setProfile({ language, audience });

    // No `viewTransition`: the profile screen is the next step inside the same
    // persistent card, and a root view transition would cross-fade the whole
    // document — photograph included — over the top of it.
    const moved = courseLanguage(audience, language) !== language;

    // The root loader holds the strings, and a navigation doesn't re-run it:
    // when the language just moved to English, reload them once it lands.
    void Promise.resolve(navigate(`/h/${themeForAudience(audience)}/profile`)).then(() =>
      moved ? revalidator.revalidate() : undefined,
    );
  };

  return (
    <div className="space-y-6">
      <StepCounter
        step={STEP}
        total={ONBOARDING_STEPS}
        label={formatString(onboarding.step_progress, { step: STEP, total: ONBOARDING_STEPS })}
      />

      <div className="space-y-3">
        <StepLogo src={siteSettings.stepLogoUrl} height={siteSettings.logoHeights.step} />
        <PartnerMark partner={siteSettings.partner} fallbackLabel={onboarding.partner_label} />
      </div>

      <div className="space-y-6">
        <motion.div {...stagedChild} className="space-y-1.5">
          <OnboardingHeading>{onboarding.audience_group_title}</OnboardingHeading>
          <p className="text-bp-ink-muted text-sm">{onboarding.audience_subtitle}</p>
        </motion.div>

        <ul className="flex flex-col gap-2">
          {audiences.map((audience) => (
            <li key={audience.value}>
              <ChoiceTile
                selected={false}
                indicator="go"
                onClick={() => chooseAudience(audience.value)}
                label={audience.label}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
